import { useState, useRef, useEffect } from "react"
import { useOrderStore } from "../state/orderStore"

interface Props { onSubmit: () => void; submitting: boolean }

export default function CartPanel({ onSubmit, submitting }: Props) {
    const items = useOrderStore(s => s.getItems())
    const total = useOrderStore(s => s.total())
    const updateQty = useOrderStore(s => s.updateQuantity)
    const updateNapomena = useOrderStore(s => s.updateNapomena)
    const removeItem = useOrderStore(s => s.removeItem)
    const clear = useOrderStore(s => s.clearAktivnu)
    const [editingNote, setEditingNote] = useState<string | null>(null)
    const [noteDraft, setNoteDraft] = useState("")
    const [editingQty, setEditingQty] = useState<string | null>(null)
    const [qtyDraft, setQtyDraft] = useState("")
    const qtyInputRef = useRef<HTMLInputElement>(null)

    const reversedItems = [...items].reverse()

    // Pametan + i - korak
    const smartIncrement = (current: number): number => {
        // Ako je decimalan, dodaj 0.5
        if (current % 1 !== 0) return Math.round((current + 0.5) * 1000) / 1000
        return current + 1
    }
    const smartDecrement = (current: number): number => {
        if (current % 1 !== 0) return Math.round((current - 0.5) * 1000) / 1000
        return current - 1
    }

    // Format količine za prikaz
    const formatQty = (q: number, jedMj: string): string => {
        if (jedMj === "KG" || jedMj === "L") {
            // 0.250 kg, 1.500 kg
            return q.toFixed(3).replace(/\.?0+$/, '') || "0"
        }
        // 0.5, 1, 2.5
        if (q % 1 === 0) return q.toString()
        return q.toString()
    }

    // Prečaci za količinu po jedinici mjere
    const getQuickQuantities = (jedMj: string): number[] => {
        if (jedMj === "KG") return [0.250, 0.500, 1, 1.5]
        if (jedMj === "L") return [0.250, 0.500, 1, 2]
        return [0.5, 1, 1.5, 2]
    }

    const startEditNote = (sifra: string, currentNote: string | undefined) => {
        setEditingNote(sifra)
        setNoteDraft(currentNote || "")
    }
    const saveNote = (sifra: string) => {
        updateNapomena(sifra, noteDraft.trim())
        setEditingNote(null)
        setNoteDraft("")
    }
    const cancelEditNote = () => {
        setEditingNote(null)
        setNoteDraft("")
    }

    const startEditQty = (sifra: string, currentQty: number) => {
        setEditingQty(sifra)
        setQtyDraft(currentQty.toString())
    }
    const saveQty = (sifra: string) => {
        // Zamijeni zarez tačkom (BiH lokalizacija)
        const normalized = qtyDraft.replace(",", ".")
        const parsed = parseFloat(normalized)
        if (!isNaN(parsed) && parsed > 0) {
            updateQty(sifra, Math.round(parsed * 1000) / 1000)  // 3 decimale max
        }
        setEditingQty(null)
        setQtyDraft("")
    }
    const cancelEditQty = () => {
        setEditingQty(null)
        setQtyDraft("")
    }

    // Auto-focus na input kad se otvori edit
    useEffect(() => {
        if (editingQty && qtyInputRef.current) {
            qtyInputRef.current.focus()
            qtyInputRef.current.select()
        }
    }, [editingQty])

    return (
        <div className="bg-antique-light border-l-2 border-antique flex flex-col h-full">
            <div className="px-4 py-3 border-b-2 border-antique bg-antique flex items-center justify-between">
                <div>
                    <h2 className="font-bold text-lg text-slate-800">Narudžba</h2>
                    {items.length > 0 && (
                        <div className="text-xs text-slate-600">{items.length} {items.length === 1 ? "stavka" : "stavki"}</div>
                    )}
                </div>
                {items.length > 0 && (
                    <button
                        onClick={clear}
                        className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded hover:bg-white/50"
                    >
                        Otkaži sve
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
                {items.length === 0 ? (
                    <div></div>
                ) : (
                    <div className="space-y-2">
                        {reversedItems.map((item, idx) => (
                            <div
                                key={item.sifra}
                                className={`bg-white rounded-lg p-3 shadow-soft border border-antique transition-all ${idx === 0 ? "ring-2 ring-primary-200" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-slate-800 truncate">{item.nazivArtikla}</div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {item.cijena.toFixed(2)} KM / {item.jedMj || "kom"}
                                        </div>
                                        {item.napomena && editingNote !== item.sifra && (
                                            <div className="text-xs italic text-orange-700 mt-1 bg-orange-50 px-2 py-1 rounded border border-orange-200">
                                                📝 {item.napomena}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => removeItem(item.sifra)}
                                        className="bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 w-9 h-9 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0 transition-colors"
                                        aria-label="Obriši"
                                    >
                                        ×
                                    </button>
                                </div>

                                {editingNote === item.sifra ? (
                                    <div className="mb-2 space-y-2">
                                        <textarea
                                            value={noteDraft}
                                            onChange={e => setNoteDraft(e.target.value)}
                                            placeholder="npr. bez luka, srednje pečeno..."
                                            className="w-full border-2 border-primary-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:outline-none resize-none"
                                            rows={2}
                                            autoFocus
                                            onKeyDown={e => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault()
                                                    saveNote(item.sifra)
                                                }
                                                if (e.key === "Escape") cancelEditNote()
                                            }}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => saveNote(item.sifra)}
                                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-semibold text-sm"
                                            >
                                                Spasi
                                            </button>
                                            <button
                                                onClick={cancelEditNote}
                                                className="px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 rounded-lg font-medium text-sm"
                                            >
                                                Odustani
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => startEditNote(item.sifra, item.napomena)}
                                        className="text-xs text-primary-600 hover:text-primary-700 font-medium mb-2 flex items-center gap-1"
                                    >
                                        {item.napomena ? "✏️ Izmijeni napomenu" : "+ Dodaj napomenu"}
                                    </button>
                                )}

                                {/* Količina */}
                                {editingQty === item.sifra ? (
                                    <div className="mb-2 bg-primary-50 border-2 border-primary-300 rounded-lg p-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                ref={qtyInputRef}
                                                type="text"
                                                inputMode="decimal"
                                                value={qtyDraft}
                                                onChange={e => setQtyDraft(e.target.value)}
                                                className="flex-1 border-2 border-primary-300 rounded-lg px-3 py-2 text-lg font-bold text-center focus:border-primary-500 focus:outline-none"
                                                onKeyDown={e => {
                                                    if (e.key === "Enter") saveQty(item.sifra)
                                                    if (e.key === "Escape") cancelEditQty()
                                                }}
                                            />
                                            <span className="text-sm text-slate-600 font-medium">{item.jedMj || "kom"}</span>
                                        </div>
                                        <div className="grid grid-cols-4 gap-1">
                                            {getQuickQuantities(item.jedMj || "KOM").map(q => (
                                                <button
                                                    key={q}
                                                    onClick={() => setQtyDraft(q.toString())}
                                                    className="bg-white hover:bg-primary-100 border border-primary-200 rounded text-sm py-1.5 font-medium text-slate-700"
                                                >
                                                    {formatQty(q, item.jedMj || "KOM")}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => saveQty(item.sifra)}
                                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-semibold text-sm"
                                            >
                                                Spasi
                                            </button>
                                            <button
                                                onClick={cancelEditQty}
                                                className="px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 rounded-lg font-medium text-sm"
                                            >
                                                Odustani
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1 bg-capInactive-light rounded-lg p-1">
                                            <button
                                                onClick={() => updateQty(item.sifra, smartDecrement(item.kolicina))}
                                                className="bg-white hover:bg-capActive-light active:bg-capActive border border-capActive rounded w-9 h-9 text-xl font-bold text-slate-700 flex items-center justify-center"
                                            >−</button>
                                            <button
                                                onClick={() => startEditQty(item.sifra, item.kolicina)}
                                                className="min-w-[3.5rem] px-2 text-center font-bold text-lg text-slate-800 hover:bg-white rounded transition-colors"
                                                title="Klikni za unos količine"
                                            >
                                                {formatQty(item.kolicina, item.jedMj || "KOM")}
                                            </button>
                                            <button
                                                onClick={() => updateQty(item.sifra, smartIncrement(item.kolicina))}
                                                className="bg-white hover:bg-capActive-light active:bg-capActive border border-capActive rounded w-9 h-9 text-xl font-bold text-slate-700 flex items-center justify-center"
                                            >+</button>
                                        </div>
                                        <div className="font-bold text-slate-800 text-lg">
                                            {(item.cijena * item.kolicina).toFixed(2)} <span className="text-sm text-slate-500">KM</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="p-4 border-t-2 border-antique bg-antique">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-700 font-medium">Ukupno:</span>
                    <span className="text-3xl font-bold text-primary-900">{total.toFixed(2)} <span className="text-lg text-slate-600">KM</span></span>
                </div>
                <button
                    onClick={onSubmit}
                    disabled={items.length === 0 || submitting}
                    className="w-full bg-primary-600 hover:bg-primary-700 active:bg-primary-900 text-white py-4 rounded-xl font-bold text-lg disabled:bg-slate-300 disabled:cursor-not-allowed shadow-medium transition-colors"
                >
                    {submitting ? "Šaljem..." : "✓ Završi i štampaj račun"}
                </button>
            </div>
        </div>
    )
}